import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ScrollText } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { fileUrl, formatDate } from "@/lib/api";
import { STRUCTURE_LABEL } from "@/lib/content";
import { PageHero, Empty, Reveal } from "@/components/kit";
import { useFetch } from "@/components/shared";
import { NewsletterForm } from "@/components/forms/NewsletterForm";
import { FilterTabs } from "./News";

const BAR = { ong: "bg-ong", association: "bg-asso", commun: "bg-gold" };

function LetterRow({ l, i }) {
  return (
    <Reveal delay={Math.min(i * 0.05, 0.3)}>
      <Link to={`/lettres/${l.slug}`} className="card group flex overflow-hidden transition-transform hover:-translate-y-0.5" data-testid={`letter-card-${l.slug}`}>
        <span className={`w-1.5 shrink-0 ${BAR[l.structure] || "bg-gold"}`} aria-hidden="true" />
        <div className="flex flex-1 flex-col gap-2 p-5 sm:flex-row sm:items-center sm:gap-6">
          <ScrollText className="hidden h-8 w-8 shrink-0 text-u sm:block" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{STRUCTURE_LABEL[l.structure]} · {formatDate(l.created_at)}</p>
            <h2 className="mt-1 font-display text-2xl font-bold uppercase leading-tight text-ink group-hover:text-u">{l.title}</h2>
            {l.excerpt && <p className="mt-1 line-clamp-2 text-neutral-600">{l.excerpt}</p>}
          </div>
          <ArrowRight className="h-5 w-5 text-u transition-transform group-hover:translate-x-1" aria-hidden="true" />
        </div>
      </Link>
    </Reveal>
  );
}

export default function Letters() {
  useSeo("Lettres d'information", "Archive des lettres d'information de l'ONG MA LEBA et de l'ASSOCIATION MA LEBA.");
  const [structure, setStructure] = useState("");
  const letters = useFetch(`/letters${structure ? `?structure=${structure}` : ""}`, [structure]);
  return (
    <div data-testid="letters-page">
      <PageHero eyebrow="Newsletter" title="Lettres d'information" intro="Retrouvez toutes nos lettres d'information publiées." />
      <section className="container-x grid gap-10 py-14 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-8">
          <FilterTabs value={structure} onChange={setStructure} testid="letters-filter"
            options={[["", "Toutes"], ["ong", "ONG"], ["association", "Association"], ["commun", "Commun"]]} />
          {(letters || []).map((l, i) => <LetterRow key={l.id} l={l} i={i} />)}
          {letters && !letters.length && <Empty testid="letters-empty">Aucune lettre publiée pour le moment.</Empty>}
        </div>
        <aside className="lg:col-span-4">
          <div className="card sticky top-28 p-6">
            <p className="font-display text-xl font-bold uppercase text-ink">Restez informé(e)</p>
            <p className="mt-1 text-sm text-neutral-600">Inscrivez-vous pour être tenu(e) au courant de nos actions.</p>
            <NewsletterForm />
          </div>
        </aside>
      </section>
    </div>
  );
}

export function LetterPage() {
  const { slug } = useParams();
  const l = useFetch(`/letters/${slug}`, [slug]);
  useSeo(l?.title, l?.excerpt);
  if (!l) return <p className="container-x py-20">Chargement…</p>;
  if (Array.isArray(l)) return <div className="container-x py-20"><Empty testid="letter-not-found">Lettre introuvable.</Empty></div>;
  return (
    <article className="pb-20" data-testid="letter-page">
      <PageHero eyebrow={`Lettre d'information · ${STRUCTURE_LABEL[l.structure]} · ${formatDate(l.created_at)}`} title={l.title} intro={l.excerpt} />
      <div className="container-x mt-12 max-w-3xl">
        {l.cover_url && <img src={fileUrl(l.cover_url)} alt="" loading="lazy" className="mb-10 w-full rounded-xl object-contain shadow" />}
        <div className="card pagne whitespace-pre-line p-6 text-lg leading-relaxed text-neutral-800 sm:p-10" data-testid="letter-content">{l.content}</div>
        <Link to="/lettres" className="btn-outline mt-12 text-u" data-testid="letter-back"><ArrowLeft className="h-4 w-4" /> Toutes les lettres</Link>
      </div>
    </article>
  );
}
